import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { supabase, saveSessionToChips, readSessionFromChips, clearChipsSession } from './lib/supabase';
import { isAccountDisabled, signOut as authSignOut } from './api/auth';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import MerchFooter from './components/layout/MerchFooter';
import PromoBanner from './components/PromoBanner';
import { showPromoStripOnPath } from './config/promo';
import Home from './pages/Home';
import LeadCatalog from './pages/LeadCatalog';
import Carriers from './pages/Carriers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import About from './pages/About';
import MeetUs from './pages/MeetUs';
import Contact from './pages/Contact';
import Apply from './pages/Apply';
import Tech from './pages/Tech';
import Services from './pages/Services';
import ServicesContact from './pages/ServicesContact';
import Store from './pages/Store';
import StoreProduct from './pages/StoreProduct';

// Implement code splitting with React.lazy for better performance
const LoginPageNew = React.lazy(() => import('./components/LoginPageNew'));
const SignUpPage = React.lazy(() => import('./components/SignUpPage'));
const ForgotPasswordPage = React.lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./components/ResetPasswordPage'));
const AuthCallback = React.lazy(() => import('./components/AuthCallback'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const MortgageProtectionPage = React.lazy(() => import('./components/products/MortgageProtectionPage'));
const ChristianLeadsPage = React.lazy(() => import('./components/products/ChristianLeadsPage'));
const VeteranLeadsPage = React.lazy(() => import('./components/products/VeteranLeadsPage'));
const TruckerLeadsPage = React.lazy(() => import('./components/products/TruckerLeadsPage'));
const InternetSurveyLeadsPage = React.lazy(() => import('./components/products/InternetSurveyLeadsPage'));
// Survey2LeadsPage and DialerSeatV0Page removed, products discontinued
const DialerSeatPage = React.lazy(() => import('./components/products/DialerSeatPage'));
const DialerSeatV2Page = React.lazy(() => import('./components/products/DialerSeatV2Page'));
const DialerSeatV3Page = React.lazy(() => import('./components/products/DialerSeatV3Page'));
const DialerSeatV4MIPage = React.lazy(() => import('./components/products/DialerSeatV4MIPage'));
const AprilMissedInboundPage = React.lazy(() => import('./components/products/AprilMissedInboundPage'));
const IULLeadsPage = React.lazy(() => import('./components/products/IULLeadsPage'));
const InboundRequestPage = React.lazy(() => import('./components/products/InboundRequestPage'));

const AgedTruckerLeadsPage = React.lazy(() => import('./components/products/aged/AgedTruckerLeadsPage'));
const AgedVeteranLeadsPage = React.lazy(() => import('./components/products/aged/AgedVeteranLeadsPage'));
// AgedMortgageProtectionLeadsPage and AgedChristianLeadsPage removed, products discontinued
const AgedIULLeadsPage = React.lazy(() => import('./components/products/aged/AgedIULLeadsPage'));
// AgedSurvey1LeadsPage and AgedSurvey2LeadsPage removed, products discontinued

// SMS Product Pages
const SMSLifeBundle50Page = React.lazy(() => import('./components/products/sms/SMSLifeBundle50Page'));
const SMSLifeBundle100Page = React.lazy(() => import('./components/products/sms/SMSLifeBundle100Page'));
const SMSLifeBundle200Page = React.lazy(() => import('./components/products/sms/SMSLifeBundle200Page'));
const SMSLifeBundle400Page = React.lazy(() => import('./components/products/sms/SMSLifeBundle400Page'));
const SMSMissedInbound010Page = React.lazy(() => import('./components/products/sms/SMSMissedInbound010Page'));
const SMSMissedInbound10PlusPage = React.lazy(() => import('./components/products/sms/SMSMissedInbound10PlusPage'));
const SMSLife25Page = React.lazy(() => import('./components/products/sms/SMSLife25Page'));
const SMSLife50Page = React.lazy(() => import('./components/products/sms/SMSLife50Page'));
const SMSLife100Page = React.lazy(() => import('./components/products/sms/SMSLife100Page'));
const SMSPositiveResponsesPage = React.lazy(() => import('./components/products/sms/SMSPositiveResponsesPage'));

// SMS Homeowner Product Pages
const SMSHomeownerBeneficiary35Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBeneficiary35Page'));
const SMSHomeownerBeneficiary50Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBeneficiary50Page'));
const SMSHomeownerBeneficiary100Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBeneficiary100Page'));
const SMSHomeownerBundle50Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBundle50Page'));
const SMSHomeownerBundle100Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBundle100Page'));
const SMSHomeownerBundle200Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBundle200Page'));
const SMSHomeownerBundle400Page = React.lazy(() => import('./components/products/sms/SMSHomeownerBundle400Page'));

const ProfilePage = React.lazy(() => import('./components/ProfilePage'));
const MyLeadsPage = React.lazy(() => import('./components/MyLeadsPage'));
const AgentDialerSubs = React.lazy(() => import('./components/AgentDialerSubs'));
const AnalyticsPage = React.lazy(() => import('./components/AnalyticsPage'));
// Import AdminPage directly (not lazy) to prevent any loading screens
import AdminPage from './components/AdminPage';

// Helper to check if route is agent portal (includes legacy /internal-store/ for redirects)
const isAgentPortalPath = (pathname: string) => {
  return pathname.startsWith('/agent/') || pathname.startsWith('/internal-store/');
};

// Preload components for faster navigation
const preloadComponents = () => {
  // Preload dashboard and login page since they're commonly accessed
  import('./components/Dashboard');
  import('./components/LoginPage');
  // Preload other commonly accessed components
  import('./pages/LeadCatalog');
  import('./pages/Carriers');
};

// Error Boundary component for better error handling
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Something went wrong</h2>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Invisible loading component - let individual pages handle their own loading
const LoadingSpinner: React.FC = () => null;

// Simple session management
const SessionManager = {
  isAuthenticated: () => {
    return sessionStorage.getItem('authenticated') === 'true';
  },
  login: () => {
    sessionStorage.setItem('authenticated', 'true');
    if (typeof gtag !== 'undefined') {
      gtag('event', 'login', {
        method: 'access_code'
      });
    }
  },
  logout: () => {
    sessionStorage.removeItem('authenticated');
    sessionStorage.removeItem('authVerified');
    if (typeof gtag !== 'undefined') {
      gtag('event', 'logout');
    }
  }
};

// Protected Route Component - simplified to prevent multiple loading screens
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hasVerifiedAuth = sessionStorage.getItem('authVerified') === 'true';
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(hasVerifiedAuth ? true : null);
  // null = status not yet checked, false = active, true = disabled/suspended.
  const [accountBlocked, setAccountBlocked] = React.useState<boolean | null>(null);
  const inIframe = window.parent !== window;

  React.useEffect(() => {
    if (hasVerifiedAuth) return;

    let cancelled = false;

    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('[ProtectedRoute] Session found in localStorage');
          if (!cancelled) {
            setIsAuthenticated(true);
            sessionStorage.setItem('authVerified', 'true');
          }
          return;
        }

        // In an iframe: try restoring from CHIPS cookies first (instant),
        // then fall back to waiting for postMessage from the parent.
        if (inIframe) {
          const chips = readSessionFromChips();
          if (chips) {
            console.log('[ProtectedRoute] Restoring session from CHIPS cookies...');
            const { error } = await supabase.auth.setSession(chips);
            if (!error) {
              console.log('[ProtectedRoute] CHIPS session restored');
              if (!cancelled) {
                setIsAuthenticated(true);
                sessionStorage.setItem('authVerified', 'true');
              }
              return;
            }
            console.warn('[ProtectedRoute] CHIPS session expired/invalid, clearing');
            clearChipsSession();
          }

          // Fall back: wait up to 4 s for parent to send RESTORE_SESSION
          console.log('[ProtectedRoute] Waiting for parent RESTORE_SESSION...');
          const restored = await new Promise<boolean>((resolve) => {
            const timeout = setTimeout(() => resolve(false), 4000);
            const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
              if (sess) {
                clearTimeout(timeout);
                subscription.unsubscribe();
                resolve(true);
              }
            });
            const poll = setInterval(async () => {
              const { data: { session: s } } = await supabase.auth.getSession();
              if (s) {
                clearInterval(poll);
                clearTimeout(timeout);
                subscription.unsubscribe();
                resolve(true);
              }
            }, 500);
            setTimeout(() => clearInterval(poll), 4000);
          });

          if (cancelled) return;
          if (restored) {
            console.log('[ProtectedRoute] Session restored via postMessage');
            setIsAuthenticated(true);
            sessionStorage.setItem('authVerified', 'true');
            return;
          }
        }

        if (!cancelled) setIsAuthenticated(false);
      } catch (error) {
        console.error('Auth check error:', error);
        if (!cancelled) setIsAuthenticated(false);
      }
    };

    checkAuth();
    return () => { cancelled = true; };
  }, [hasVerifiedAuth, inIframe]);

  // Enforce account status on every mount that has a confirmed session. This
  // catches already-logged-in and session-restored (CHIPS / iframe) agents who
  // were disabled after their session was established — a valid session is no
  // longer enough; the account must also be active. Runs independently of the
  // authVerified cache so a mid-session disable is honored on the next page.
  React.useEffect(() => {
    if (isAuthenticated !== true) return;

    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return;
        const blocked = await isAccountDisabled(user.id, user.email);
        if (cancelled) return;
        if (blocked) {
          sessionStorage.setItem('accountDisabled', 'true');
          sessionStorage.removeItem('authVerified');
          await authSignOut();
          if (!cancelled) setAccountBlocked(true);
        } else {
          setAccountBlocked(false);
        }
      } catch (error) {
        // Fail open: never lock an agent out over a transient status-check error.
        console.error('Account status check error:', error);
        if (!cancelled) setAccountBlocked(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (isAuthenticated === false || accountBlocked === true) {
    return <Navigate to="/agent/login" replace />;
  }

  return <>{children}</>;
};

const isClosioOrigin = (origin: string) =>
  origin === 'https://closio.com' || origin.endsWith('.closio.com');
const isInIframe = () => window.parent !== window;
const getParentOrigin = () => {
  try {
    const ref = document.referrer;
    if (ref) {
      const url = new URL(ref);
      if (isClosioOrigin(url.origin)) return url.origin;
    }
  } catch { /* ignore */ }
  return 'https://closio.com';
};

function PathReporter() {
  const location = useLocation();

  React.useEffect(() => {
    if (isInIframe()) {
      window.parent.postMessage(
        { type: 'ENHANCE_PATH', path: location.pathname },
        getParentOrigin()
      );
    }
  }, [location.pathname]);

  return null;
}

function ClosioSessionListener() {
  const nav = useNavigate();

  React.useEffect(() => {
    if (!isInIframe()) return;

    const handler = async (event: MessageEvent) => {
      console.log('[iframe] postMessage received:', event.data?.type, 'from', event.origin);
      if (!isClosioOrigin(event.origin)) return;
      if (event.data?.type !== 'RESTORE_SESSION') return;

      console.log('[iframe] Restoring session from parent...');
      const { error } = await supabase.auth.setSession({
        access_token: event.data.token,
        refresh_token: event.data.refreshToken,
      });

      if (!error) {
        console.log('[iframe] Session restored successfully');
        sessionStorage.setItem('authenticated', 'true');
        sessionStorage.setItem('authVerified', 'true');
        if (event.data.path) {
          nav(event.data.path);
        }
      } else {
        console.error('[iframe] Session restore failed:', error);
      }
    };

    window.addEventListener('message', handler);

    console.log('[iframe] Sending REQUEST_SESSION to parent:', getParentOrigin());
    window.parent.postMessage({ type: 'REQUEST_SESSION' }, getParentOrigin());

    return () => window.removeEventListener('message', handler);
  }, [nav]);

  React.useEffect(() => {
    if (!isInIframe()) return;
    const parentOrigin = getParentOrigin();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        saveSessionToChips(session.access_token, session.refresh_token);
        window.parent.postMessage({
          type: 'ENHANCE_AUTH',
          token: session.access_token,
          refreshToken: session.refresh_token,
          currentPath: window.location.pathname,
        }, parentOrigin);
      } else {
        clearChipsSession();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return null;
}

function App() {
  const location = useLocation();

  const isAgentPortal = isAgentPortalPath(location.pathname);

  // Preload components on app start for better performance
  React.useEffect(() => {
    const timer = setTimeout(preloadComponents, 100);
    return () => clearTimeout(timer);
  }, []);

  // Clear session on page refresh or initial load (skip in iframe, session persists via cookies/postMessage)
  React.useEffect(() => {
    if (window.parent !== window) return;

    const handleBeforeUnload = () => {
      SessionManager.logout();
    };

    if (!location.pathname.startsWith('/agent/login') && !SessionManager.isAuthenticated()) {
      // Let the ProtectedRoute component handle the redirect
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Add page titles for better SEO and navigation
  React.useEffect(() => {
    const titles: Record<string, string> = {
      // Agent Portal
      '/agent/login': 'Enhance Lead Store - Login',
      '/agent/signup': 'Enhance Lead Store - Sign Up',
      '/agent/forgot-password': 'Enhance Lead Store - Forgot Password',
      '/agent/reset-password': 'Enhance Lead Store - Reset Password',
      '/agent/dashboard': 'Enhance Lead Store - Dashboard',
      '/agent/profile': 'Enhance Lead Store - Profile',
      '/agent/my-leads': 'Enhance Lead Store - My Leads',
      '/agent/admin': 'Enhance Lead Store - Admin',
      // Fresh Products
      '/agent/products/mortgage-protection': 'Fresh Mortgage Protection Leads',
      '/agent/products/christian-leads': 'Fresh Christian Leads',
      '/agent/products/veteran-leads': 'Fresh Veteran Leads', 
      '/agent/products/trucker-leads': 'Fresh Trucker Leads',
      '/agent/products/internet-survey': 'Volume Internet Quote Leads',
      '/agent/products/dialer-seat': 'Dialer Seat V.1',
      '/agent/products/dialer-seat-v2': 'Dialer Seat V.2',
      '/agent/products/dialer-seat-v3': 'Dialer Seat V.3 - IUL Leads',
      '/agent/products/dialer-seat-v4-mi': 'Enhance Dialer Seat V.4- Missed Inbound',
      '/agent/products/april-missed-inbound': 'November Missed Inbound Dialer',
      '/agent/products/iul-leads': 'IUL Leads',
      // FEX Inbound Requests
      '/agent/products/fex-inbound-longer': 'FEX Inbound Leads (Longer Buffer)',
      '/agent/products/fex-inbound-shorter': 'FEX Inbound Leads (Shorter Buffer)',
      '/agent/products/fex-live-transfer': 'FEX Live Transfer',
      // Aged Products
      '/agent/aged/trucker-leads': 'Aged Trucker Leads',
      '/agent/aged/veteran-leads': 'Aged Veteran Leads',
      '/agent/aged/iul-leads': 'Aged IUL Leads',
      // SMS Products
      '/agent/sms/life-bundle-50': 'SMS-LIFE Bundle of 50',
      '/agent/sms/life-bundle-100': 'SMS-LIFE Bundle of 100',
      '/agent/sms/life-bundle-200': 'SMS-LIFE Bundle of 200',
      '/agent/sms/life-bundle-400': 'SMS-LIFE Bundle of 400',
      '/agent/sms/life-beneficiary-25': 'SMS-LIFE w/ Beneficiary (25 Leads)',
      '/agent/sms/life-beneficiary-50': 'SMS-LIFE w/ Beneficiary (50 Leads)',
      '/agent/sms/life-beneficiary-100': 'SMS-LIFE w/ Beneficiary (100 Leads)',
      '/agent/sms/positive-responses': 'A.I. SMS Positive Responses',
      // SMS Homeowner Products
      '/agent/sms/homeowner-beneficiary-35': 'SMS-LIFE Homeowners w/ Beneficiary (25 Leads)',
      '/agent/sms/homeowner-beneficiary-50': 'SMS-LIFE Homeowners w/ Beneficiary (50 Leads)',
      '/agent/sms/homeowner-beneficiary-100': 'SMS-LIFE Homeowners w/ Beneficiary (100 Leads)',
      '/agent/sms/homeowner-bundle-50': 'SMS-LIFE Homeowner Bundle of 50',
      '/agent/sms/homeowner-bundle-100': 'SMS-LIFE Homeowner Bundle of 100',
      '/agent/sms/homeowner-bundle-200': 'SMS-LIFE Homeowner Bundle of 200',
      '/agent/sms/homeowner-bundle-400': 'SMS-LIFE Homeowner Bundle of 400',
      '/agent/products/missed-inbound-0-10': 'Missed Inbound Leads 0-10 Day',
      '/agent/aged/missed-inbound-10-plus': 'Missed Inbound Leads 10+ Day',
      // Public Pages
      '/': 'Enhance Companies - Insurance Agency',
      '/lead-catalog': 'Lead Catalog - Enhance Companies',
      '/carriers': 'Carrier Partners - Enhance Companies',
      '/about': 'About Us - Enhance Companies',
      '/for-agencies': 'For Agencies - Enhance Companies',
      '/contact': 'Contact Us - Enhance Companies',
      '/apply': 'Apply Now - Enhance Companies',
      '/technology': 'Our Technology - Enhance Companies',
      '/services': 'Services - Enhance Companies',
      '/services/contact': 'Get Started - Enhance Companies',
      '/store': 'Shop - Enhance Companies'
    };
    
    document.title = titles[location.pathname] || 'Enhance Companies';
  }, [location.pathname]);

  // The promo strip shows on lead-store pages only, not the sign-in screens.
  const showPromoStrip = showPromoStripOnPath(location.pathname);

  if (isAgentPortal) {
    return (
      <ErrorBoundary>
        <div className={`App ${showPromoStrip ? 'pt-9' : ''}`}>
          {showPromoStrip && <PromoBanner variant="strip" />}
          <ClosioSessionListener />
          <PathReporter />
          <React.Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* ===== Agent Portal Routes ===== */}
              {/* Auth Pages */}
              <Route path="/agent/login" element={<LoginPageNew />} />
              <Route path="/agent/signup" element={<SignUpPage />} />
              <Route path="/agent/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/agent/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/confirm" element={<AuthCallback />} />
              
              {/* Dashboard & Profile */}
              <Route path="/agent/dashboard" element={
                <ProtectedRoute>
                  <Dashboard onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/profile" element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="/agent/my-leads" element={
                <ProtectedRoute>
                  <MyLeadsPage />
                </ProtectedRoute>
              } />
              <Route path="/agent/analytics" element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/agent/dialer-subs" element={
                <ProtectedRoute>
                  <AgentDialerSubs />
                </ProtectedRoute>
              } />
              <Route path="/agent/admin" element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              } />
              
              {/* Fresh Product Routes */}
              <Route path="/agent/products/mortgage-protection" element={
                <ProtectedRoute>
                  <MortgageProtectionPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/christian-leads" element={
                <ProtectedRoute>
                  <ChristianLeadsPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/veteran-leads" element={
                <ProtectedRoute>
                  <VeteranLeadsPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/trucker-leads" element={
                <ProtectedRoute>
                  <TruckerLeadsPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/internet-survey" element={
                <ProtectedRoute>
                  <InternetSurveyLeadsPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              {/* Discontinued products redirect to dashboard */}
              <Route path="/agent/products/survey-2" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/agent/products/dialer-seat-v0" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/agent/products/dialer-seat" element={
                <ProtectedRoute>
                  <DialerSeatPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/dialer-seat-v2" element={
                <ProtectedRoute>
                  <DialerSeatV2Page onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/dialer-seat-v3" element={
                <ProtectedRoute>
                  <DialerSeatV3Page onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/dialer-seat-v4-mi" element={
                <ProtectedRoute>
                  <DialerSeatV4MIPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/april-missed-inbound" element={
                <ProtectedRoute>
                  <AprilMissedInboundPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/iul-leads" element={
                <ProtectedRoute>
                  <IULLeadsPage onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />

              {/* FEX Inbound / Live Transfer request pages */}
              <Route path="/agent/products/fex-inbound-longer" element={
                <ProtectedRoute>
                  <InboundRequestPage slug="fex-inbound-longer" onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/fex-inbound-shorter" element={
                <ProtectedRoute>
                  <InboundRequestPage slug="fex-inbound-shorter" onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/fex-live-transfer" element={
                <ProtectedRoute>
                  <InboundRequestPage slug="fex-live-transfer" onLogout={SessionManager.logout} />
                </ProtectedRoute>
              } />

              {/* Aged Product Routes */}
              <Route path="/agent/aged/trucker-leads" element={
                <ProtectedRoute>
                  <AgedTruckerLeadsPage />
                </ProtectedRoute>
              } />
              <Route path="/agent/aged/veteran-leads" element={
                <ProtectedRoute>
                  <AgedVeteranLeadsPage />
                </ProtectedRoute>
              } />
              <Route path="/agent/aged/mortgage-protection" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/agent/aged/christian-leads" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/agent/aged/iul-leads" element={
                <ProtectedRoute>
                  <AgedIULLeadsPage />
                </ProtectedRoute>
              } />
              <Route path="/agent/aged/survey-1" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/agent/aged/survey-2" element={<Navigate to="/agent/dashboard" replace />} />

              {/* ===== SMS Product Routes ===== */}
              <Route path="/agent/sms/life-bundle-50" element={
                <ProtectedRoute>
                  <SMSLifeBundle50Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/life-bundle-100" element={
                <ProtectedRoute>
                  <SMSLifeBundle100Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/life-bundle-200" element={
                <ProtectedRoute>
                  <SMSLifeBundle200Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/life-bundle-400" element={
                <ProtectedRoute>
                  <SMSLifeBundle400Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/products/missed-inbound-0-10" element={
                <ProtectedRoute>
                  <SMSMissedInbound010Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/aged/missed-inbound-10-plus" element={
                <ProtectedRoute>
                  <SMSMissedInbound10PlusPage />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/life-beneficiary-25" element={
                <ProtectedRoute>
                  <SMSLife25Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/life-beneficiary-50" element={
                <ProtectedRoute>
                  <SMSLife50Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/life-beneficiary-100" element={
                <ProtectedRoute>
                  <SMSLife100Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/positive-responses" element={
                <ProtectedRoute>
                  <SMSPositiveResponsesPage />
                </ProtectedRoute>
              } />

              {/* ===== SMS Homeowner Product Routes ===== */}
              <Route path="/agent/sms/homeowner-beneficiary-35" element={
                <ProtectedRoute>
                  <SMSHomeownerBeneficiary35Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/homeowner-beneficiary-50" element={
                <ProtectedRoute>
                  <SMSHomeownerBeneficiary50Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/homeowner-beneficiary-100" element={
                <ProtectedRoute>
                  <SMSHomeownerBeneficiary100Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/homeowner-bundle-50" element={
                <ProtectedRoute>
                  <SMSHomeownerBundle50Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/homeowner-bundle-100" element={
                <ProtectedRoute>
                  <SMSHomeownerBundle100Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/homeowner-bundle-200" element={
                <ProtectedRoute>
                  <SMSHomeownerBundle200Page />
                </ProtectedRoute>
              } />
              <Route path="/agent/sms/homeowner-bundle-400" element={
                <ProtectedRoute>
                  <SMSHomeownerBundle400Page />
                </ProtectedRoute>
              } />

              {/* ===== Legacy URL Redirects (for bookmarks/backcompat) ===== */}
              <Route path="/internal-store/login" element={<Navigate to="/agent/login" replace />} />
              <Route path="/internal-store/signup" element={<Navigate to="/agent/signup" replace />} />
              <Route path="/internal-store/forgot-password" element={<Navigate to="/agent/forgot-password" replace />} />
              <Route path="/internal-store/reset-password" element={<Navigate to="/agent/reset-password" replace />} />
              <Route path="/internal-store/dashboard" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/internal-store/profile" element={<Navigate to="/agent/profile" replace />} />
              <Route path="/internal-store/admin" element={<Navigate to="/agent/admin" replace />} />
              <Route path="/internal-store/products/*" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/internal-store/aged-products/*" element={<Navigate to="/agent/dashboard" replace />} />
              <Route path="/access-store" element={<Navigate to="/agent/login" replace />} />

              {/* Catch all - redirect to login */}
              <Route path="*" element={<Navigate to="/agent/login" replace />} />
            </Routes>
          </React.Suspense>
        </div>
      </ErrorBoundary>
    );
  }

  // Render new light theme website
  return (
    <ErrorBoundary>
      <div className="App">
        <Navbar />
        <React.Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* ===== Public Website Routes ===== */}
            <Route path="/" element={<Home />} />
            <Route path="/lead-catalog" element={<LeadCatalog />} />
            <Route path="/carriers" element={<Carriers />} />
            <Route path="/about" element={<About />} />
            <Route path="/for-agencies" element={<MeetUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/technology" element={<Tech />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/contact" element={<ServicesContact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* ===== Merch Store ===== */}
            <Route path="/store" element={<Store />} />
            <Route path="/store/:slug" element={<StoreProduct />} />

            {/* ===== Legacy URL Redirects ===== */}
            <Route path="/store/product/:id" element={<Navigate to="/lead-catalog" replace />} />
            <Route path="/meetus" element={<Navigate to="/for-agencies" replace />} />
            <Route path="/tech" element={<Navigate to="/technology" replace />} />
            <Route path="/access-store" element={<Navigate to="/agent/login" replace />} />

            {/* Catch all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
        {location.pathname.startsWith('/store') ? <MerchFooter /> : <Footer />}
      </div>
    </ErrorBoundary>
  );
}

export { SessionManager };
export default App;